// @ts-nocheck
import React from 'react';
import { Clock, User, MessageSquare, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';

const TicketHistory = ({ ticket, userRole }) => {
  const formatDate = (date) => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Invalid Date';
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (date) => {
    const now = new Date();
    const ticketDate = new Date(date);
    if (isNaN(ticketDate.getTime())) return 'Invalid Date';
    const diffInHours = Math.floor((now - ticketDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Less than an hour ago';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    return formatDate(date);
  };

  // Normalize incoming date values to an ISO string (accepts unix seconds, ms, or ISO strings)
  const normalizeDate = (value) => {
    if (!value && value !== 0) return null;
    // If already a Date
    if (value instanceof Date) return value.toISOString();
    // If numeric (seconds or milliseconds)
    if (typeof value === 'number') {
      // likely seconds if < 1e12
      const ts = value < 1e12 ? value * 1000 : value;
      return new Date(ts).toISOString();
    }
    // If string, try parsing ISO or YYYY-MM-DD
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return parsed.toISOString();
    return null;
  };

  // Generate timeline events from ticket data
  const generateTimelineEvents = () => {
    const events = [];

    // Ticket creation (support `createdAt` or `date` fields)
    const createdAt = normalizeDate(ticket.createdAt ?? ticket.date ?? ticket.created_at);
    events.push({
      id: 'created',
      type: 'created',
      timestamp: createdAt,
      title: 'Ticket Created',
      description: ticket.customerName ?? ticket.customer ?? null,
      icon: MessageSquare,
      color: 'blue'
    });

    // First response
    const firstResponseAt = normalizeDate(ticket.firstResponseAt ?? ticket.first_response_at);
    if (firstResponseAt) {
      events.push({
        id: 'first_response',
        type: 'response',
        timestamp: firstResponseAt,
        title: 'First Response',
        description: `Responded by ${ticket.assignedAgentName || 'Support Team'}`,
        icon: User,
        color: 'green'
      });
    }

    // Status changes (simulated based on current status)
    if (ticket.status === 'in_progress' || ticket.status === 'resolved' || ticket.status === 'closed') {
      const statusChangeTime = normalizeDate(ticket.firstResponseAt ?? ticket.updatedAt ?? ticket.updated_at ?? ticket.date);
      events.push({
        id: 'status_in_progress',
        type: 'status_change',
        timestamp: statusChangeTime,
        title: 'Status Changed',
        description: `Changed to ${ticket.status.replace('_', ' ')}`,
        icon: ArrowRight,
        color: 'yellow'
      });
    }

    // Escalation events
    if ((ticket.escalationLevel ?? 0) > 0) {
      const escalatedAt = normalizeDate(ticket.updatedAt ?? ticket.updated_at ?? ticket.date);
      events.push({
        id: 'escalated',
        type: 'escalation',
        timestamp: escalatedAt,
        title: 'Ticket Escalated',
        description: `Escalated to level ${ticket.escalationLevel}`,
        icon: AlertTriangle,
        color: 'orange'
      });
    }

    // Resolution
    const resolvedAt = normalizeDate(ticket.resolvedAt ?? ticket.resolved_at);
    if (resolvedAt) {
      events.push({
        id: 'resolved',
        type: 'resolved',
        timestamp: resolvedAt,
        title: 'Ticket Resolved',
        description: `Resolved by ${ticket.assignedAgentName || 'Support Team'}`,
        icon: CheckCircle,
        color: 'green'
      });
    }

    // Sort events by timestamp (null timestamps go last)
    return events.sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : Infinity;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : Infinity;
      return ta - tb;
    });
  };

  const timelineEvents = generateTimelineEvents();

  const getIconColor = (color) => {
    const colors = {
      blue: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
      green: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20',
      yellow: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
      orange: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20',
      red: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
    };
    return colors[color] || colors.blue;
  };

  if (timelineEvents.length === 0) {
    return null;
  }

  return (
    <div className="p-6 border-b border-line-subtle dark:border-white/[0.06]">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-4 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5" />
        Timeline & Status Changes
      </h3>
      
      <div className="flow-root">
        <ul className="-mb-8">
          {timelineEvents.map((event, eventIdx) => (
            <li key={event.id}>
              <div className="relative pb-8">
                {eventIdx !== timelineEvents.length - 1 ? (
                  <span
                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-100 dark:bg-white/[0.06]"
                    aria-hidden="true"
                  />
                ) : null}
                <div className="relative flex space-x-3">
                  <div>
                    <span className={`h-8 w-8 rounded-xl flex items-center justify-center ring-4 ring-white dark:ring-dark-800 ${getIconColor(event.color)}`}>
                      <event.icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                    <div>
                      <p className="text-sm font-semibold text-content dark:text-white">
                        {event.title}
                      </p>
                      {event.description ? (
                        <p className="text-sm text-content-muted dark:text-gray-400">
                          {typeof event.description === 'string' ? `Created by ${event.description}` : event.description}
                        </p>
                      ) : null}
                    </div>
                    <div className="whitespace-nowrap text-right text-xs text-content-subtle dark:text-gray-500">
                      <time dateTime={event.timestamp}>
                        {formatRelativeTime(event.timestamp)}
                      </time>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Performance Metrics for agents/managers */}
      {userRole !== 'customer' && (
        <div className="mt-6 pt-4 border-t border-line-subtle dark:border-white/[0.06]">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-content-muted dark:text-gray-500 mb-3">Performance Metrics</h4>
          <div className="grid grid-cols-2 gap-4">
            {ticket.firstResponseAt && (
              <div className="p-3 bg-gray-50 dark:bg-white/[0.03] rounded-xl border border-line-subtle dark:border-white/[0.06]">
                <span className="text-xs text-content-muted dark:text-gray-500">Response Time</span>
                <div className="font-semibold text-content dark:text-white mt-0.5">
                  {Math.round((new Date(ticket.firstResponseAt) - new Date(ticket.createdAt)) / (1000 * 60 * 60))}h
                </div>
              </div>
            )}
            {ticket.resolvedAt && (
              <div className="p-3 bg-gray-50 dark:bg-white/[0.03] rounded-xl border border-line-subtle dark:border-white/[0.06]">
                <span className="text-xs text-content-muted dark:text-gray-500">Resolution Time</span>
                <div className="font-semibold text-content dark:text-white mt-0.5">
                  {Math.round((new Date(ticket.resolvedAt) - new Date(ticket.createdAt)) / (1000 * 60 * 60))}h
                </div>
              </div>
            )}
            <div className="p-3 bg-gray-50 dark:bg-white/[0.03] rounded-xl border border-line-subtle dark:border-white/[0.06]">
              <span className="text-xs text-content-muted dark:text-gray-500">Interactions</span>
              <div className="font-semibold text-content dark:text-white mt-0.5">
                {(ticket.replies || []).length} replies
              </div>
            </div>
            {ticket.satisfactionRating && (
              <div className="p-3 bg-gray-50 dark:bg-white/[0.03] rounded-xl border border-line-subtle dark:border-white/[0.06]">
                <span className="text-xs text-content-muted dark:text-gray-500">Satisfaction</span>
                <div className="font-semibold text-content dark:text-white mt-0.5">
                  {ticket.satisfactionRating}/5 ★
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketHistory;